// Shared payment-badge set, used everywhere an order is shown (list rows,
// detail views) for both dispatch and relocation orders — these fields are
// plain strings/booleans on the order object itself (see backend's
// OrderService::formatOrder / RelocationOrderService::formatOrder).
//
// Two badges, each answering a different question — keep them separate,
// don't collapse them into one status:
//   - payment status:     has THIS order been paid yet? For wallet orders
//                          this is just "Paid" / "Unpaid" (payment_status).
//                          For cash orders it's a richer 4-state readout
//                          (see cashStatus below) that replaces the plain
//                          Paid/Unpaid badge entirely, since payment_status
//                          alone can't distinguish "customer says they paid
//                          but the driver hasn't confirmed yet" from a plain
//                          unpaid order.
//   - payment_preference: when is it supposed to be paid, locked in at the
//                          moment it was placed? ("before_delivery" / "after_delivery")
//
// An "after_delivery" order sitting at in_transit/delivered while still
// "unpaid" is expected, not an error — don't read anything alarming into
// that combination.

const STATUS_STYLES: Record<string, string> = {
  paid: "bg-green-50 text-green-700 border border-green-300",
  unpaid: "bg-red-50 text-red-700 border border-red-300",
  pending: "bg-yellow-50 text-yellow-700 border border-yellow-300",
};

const TIMING_LABELS: Record<string, string> = {
  before_delivery: "Pay before delivery",
  after_delivery: "Pay after delivery",
};

const BADGE_CLASS = "text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap";

interface PaymentBadgesProps {
  paymentStatus?: string | null;
  paymentPreference?: string | null;
  paymentMethod?: string | null;
  cashCollected?: boolean | null;
  customerConfirmedCashAt?: string | null;
  className?: string;
}

// The cash-specific status readout: four states, checked in priority order.
// A driver-reported mismatch always wins, even over a "paid" payment_status,
// since it's the most urgent thing for the company to see and act on.
const getCashStatus = (
  paymentStatus?: string | null,
  cashCollected?: boolean | null,
  customerConfirmedCashAt?: string | null
): { label: string; style: string } => {
  if (cashCollected === false) {
    return { label: "Driver reported cash not received", style: "bg-red-50 text-red-700 border border-red-300" };
  }
  if (paymentStatus === "paid") {
    return { label: "Paid (cash)", style: "bg-green-50 text-green-700 border border-green-300" };
  }
  if (customerConfirmedCashAt && cashCollected == null) {
    return { label: "Customer says paid — awaiting driver", style: "bg-amber-50 text-amber-700 border border-amber-300" };
  }
  return { label: "Unpaid", style: "bg-red-50 text-red-700 border border-red-300" };
};

export const PaymentBadges = ({
  paymentStatus,
  paymentPreference,
  paymentMethod,
  cashCollected,
  customerConfirmedCashAt,
  className,
}: PaymentBadgesProps) => {
  const isCash = paymentMethod === "cash";

  const cash = isCash ? getCashStatus(paymentStatus, cashCollected, customerConfirmedCashAt) : null;
  const statusLabel = !isCash && paymentStatus ? paymentStatus.charAt(0).toUpperCase() + paymentStatus.slice(1) : null;
  const timingLabel = paymentPreference ? TIMING_LABELS[paymentPreference] ?? null : null;

  if (!statusLabel && !timingLabel && !cash) return null;

  return (
    <div className={`flex flex-wrap items-center gap-1.5 ${className ?? ""}`}>
      {cash && <span className={`${BADGE_CLASS} ${cash.style}`}>{cash.label}</span>}
      {statusLabel && (
        <span className={`${BADGE_CLASS} ${(paymentStatus && STATUS_STYLES[paymentStatus]) ?? "bg-gray-100 text-gray-600 border border-gray-300"}`}>
          {statusLabel}
        </span>
      )}
      {timingLabel && <span className={`${BADGE_CLASS} bg-gray-100 text-gray-600 border border-gray-300`}>{timingLabel}</span>}
    </div>
  );
};

export default PaymentBadges;
